"use client";

/**
 * @file        CarStickyBar.tsx
 * @description Mobile-only sticky bottom bar for the car-detail page.
 *              Shows price (or fallback "Цена по запросу") + primary CTA
 *              that smooth-scrolls to the in-page lead-form anchor. Sits
 *              at the end of main content via `position: sticky`, not
 *              fixed — preserves layout-flow and plays well with iOS
 *              safe-area-inset. See `knowledge/design-system.md → Sticky
 *              Bottom Bar (mobile)` for the canonical pattern spec.
 *
 * @rule        Mobile-only via `md:hidden`. On desktop, the always-visible
 *              sidebar carries price + lead-form already — a second sticky
 *              CTA would compete with it and add noise.
 *
 * @rule        `position: sticky; bottom: 0` (NOT `position: fixed`).
 *              Sticky preserves page layout-flow and avoids the
 *              safe-area-inset-bottom conflict on iPhone (Home Indicator).
 *              Fixed-positioning here was tried earlier on other pages
 *              and broke iframe-based tools (Visualizer); do NOT
 *              regress.
 *
 * @rule        `data-fm-hide="true"` is required — FloatingMessengers /
 *              StickyAskBar observe elements with this attribute and hide
 *              themselves while one is in viewport. Without it, the FAB
 *              and the sticky CTA stack on narrow screens and fight for
 *              the same touch zone.
 *
 * @rule        CTA does scroll-to-anchor, NOT open a modal. The in-page
 *              anchor (`#lead-form` by default) MUST exist on the page —
 *              wiring is done by the page that mounts this bar (prompt
 *              13). If the anchor is missing, the click is a no-op
 *              (silent), never throws.
 */

interface CarStickyBarProps {
  priceRub?: number;
  fallbackLabel?: string;
  anchorId?: string;
  ctaLabel?: string;
}

export default function CarStickyBar({
  priceRub,
  fallbackLabel = "Цена по запросу",
  anchorId = "lead-form",
  ctaLabel = "Оставить заявку",
}: CarStickyBarProps) {
  const handleScroll = () => {
    if (typeof document === "undefined") return;
    const el = document.getElementById(anchorId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div
      className="sticky bottom-0 z-30 md:hidden bg-white border-t border-border"
      data-fm-hide="true"
    >
      <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading text-lg text-primary truncate">
            {priceRub ? `≈ ${priceRub.toLocaleString("ru-RU")} ₽` : fallbackLabel}
          </div>
          {priceRub && <div className="text-xs text-text-muted">под ключ</div>}
        </div>
        <button
          type="button"
          onClick={handleScroll}
          className="px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary-hover transition-colors shrink-0"
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}
